// import dependencies
import cookies from 'js-cookie';
import loginModal from '../loginModal';
import api from '../../marketing/api';
import { delimiters } from '../constants';
import { each, selectAll, select } from '../utils/dom';

/**
 * Helper function.
 * Will emit the logged in status event to any Vue apps subscribing.
 * @param {object} status
 * @param {boolean} status.authenticated whether or not the user is logged in
 * @param {string} status.displayName optional
 * @param {string} status.userEmail optional
 * @param {string} status.notificationCount optional
 */
function emitLoggedIn(status) {
  Event.$emit('LoggedInStatus', status);
}

/**
 * Helper function.
 * When called, will check the portal for the logged in state of the user
 * and emit an event with that info so that any Vue apps that care can use
 * that info.
 */
function getLoggedInState() {
  // if we've checked for the logged in status and found the user to be logged in before,
  // their info will be stored in a cookie.
  const cookieName = 'isAuthenticated';
  const cookie = cookies.getJSON(cookieName);

  if (cookie) {
    // We've already checked during this user's session, so just let any Vue apps that care know
    // about the user's logged in state.
    emitLoggedIn({
      displayName: cookie.displayName,
      userEmail: cookie.userEmail,
      notificationCount: cookie.notificationCount,
      authenticated: true,
    });
  } else {
    // check for the logged in status
    api.get('/status', {}, false)
      .then(response => {
        if (response && typeof (response.data) === 'object' && response.data.loggedIn) {
          // set a cookie, which expires in 5 mins, with the response to avoid subsequent calls
          // https://github.com/js-cookie/js-cookie/wiki/Frequently-Asked-Questions#expire-cookies-in-less-than-a-day
          const expires = new Date(new Date().getTime() + 5 * 60 * 1000); // eslint-disable-line no-mixed-operators
          const baseDomain = location.hostname.split(".").slice(-2).join(".");
          cookies.set(cookieName, {
            displayName: response.data.displayName,
            userEmail: '', // response.data.Email,
            notificationCount: response.data.notificationCount,
          }, { expires, secure: true, SameSite: "strict", Domain: baseDomain });

          // Let any Vue apps that care know about the logged in state.
          emitLoggedIn({
            displayName: response.data.displayName,
            userEmail: '', // response.data.Email,
            notificationCount: response.data.notificationCount,
            authenticated: true,
          });
        } else {
          // status endpoint has been updated to return 200 when user isn't logged in
          // instead of a 400 error
          // with a loggedIn property that will be false
          // set a cookie, which expires in 5 mins, so server doesn't have to continue loading this app
          const expires = new Date(new Date().getTime() + 5 * 60 * 1000); // eslint-disable-line no-mixed-operators
          const baseDomain = location.hostname.split(".").slice(-2).join(".");
          cookies.set(cookieName, false, { expires, Domain: baseDomain });

          // Let any Vue apps that care know about the non-logged-in state.
          const username = cookies.get('RememberMe', { domain: baseDomain });
          emitLoggedIn({
            authenticated: false,
            userName: username,
          });
        }
      })
      .catch(() => {
        // if the status call fails, still set cookie that user isn't logged in
        const expires = new Date(new Date().getTime() + 5 * 60 * 1000); // eslint-disable-line no-mixed-operators
        const baseDomain = location.hostname.split(".").slice(-2).join(".");
        cookies.set(cookieName, false, { expires, Domain: baseDomain });

        // Let any Vue apps that care know about the non-logged-in state.
        emitLoggedIn({
          authenticated: false,
        });
      });
  }
}

/**
 * Helper function to get the first letter of a word.
 * @param {string}
 * @return {string}
 */
function getFirstLetter(str) {
  if (str && str.length) {
    return str.substring(0, 1);
  }
  return '';
}

/**
 * Data for the Vue app
 */
function data() {
  return {
    /**
     * the authenticated status of the user into the portal will determine the display of the app
     */
    authenticated: null,

    /**
     * the value from the API response for a logged in user
     */
    displayName: '',

    /**
     * the value from the API response for a logged in user
     */
    userEmail: '',

    /**
     * the number of notifications the user has
     */
    notificationCount: 0,

    /**
     * ready state of the app used to toggle css
     */
    isReady: false,

    /**
     * Fill in username when they have enabled rememberMe
     */
    userName: '',
  };
}

/**
 * Computed properties
 */
const computed = {
  /**
   * Simple computed property to tell if notifications exist
   */
  hasNotifications() {
    return parseInt(this.notificationCount, 10) > 0;
  },

  /**
   * Initials from displayName
   */
  initials() {
    let initials = '';
    if (this.displayName !== '') {
      const names = this.displayName.split(' ');
      if (names.length > 1) {
        initials = `${getFirstLetter(names[0])}${getFirstLetter(names[(names.length - 1)])}`;
      } else {
        initials = `${getFirstLetter(names[0])}`;
      }
    }
    return initials;
  },
};

/**
 * Created function
 */
function created() {
  // We care about the LoggedInStatus event that will be emitted once the AJAX request finishes.
  Event.$on('LoggedInStatus', ({ displayName, userEmail, notificationCount, authenticated, userName }) => {
    this.authenticated = authenticated;
    this.userName = userName;
    if (authenticated) {
      this.displayName = displayName;
      this.userEmail = userEmail;
      this.notificationCount = notificationCount;
    }
    // app is ready, so remove loading state
    this.isReady = true;
  });
}

/**
 * Watch functions
 */
const watch = {
  authenticated(value) {
    if (value !== null) {
      Vue.nextTick(() => {
        // reinitialize the login modal because the button is there now.
        loginModal();
      });
    }
  },
};

/**
 * Adjust the padding of main content
 */
function adjustPadding() {
  setTimeout(() => {
    const DOM = {
      desktopHeader: select('.SiteHeader--desktop'),
      mobileHeader: select('.SiteHeader--mobile'),
      main: select('main'),
    };
    if (DOM.main) {
      let headerHeight = DOM.desktopHeader.style.top + DOM.desktopHeader.clientHeight;

      if (DOM.desktopHeader.clientHeight === 0) {
        headerHeight = DOM.mobileHeader.style.top + DOM.mobileHeader.clientHeight;
      }

      DOM.main.style.paddingTop = `${(headerHeight)}px`;
    }
  }, 500);
}

const mounted = function () {
  adjustPadding();
};
// Logged in user apps
const buttons = selectAll('[data-vue-app="logged-in-user"]');
if (buttons && buttons.length) {
  // initialize the apps
  each(buttons, (el) => {
    new Vue({ // eslint-disable-line no-new
      delimiters,
      el,
      data,
      computed,
      created,
      watch,
      mounted,
    });
  });

  // now that apps have been initialized, make the AJAX call to get logged in state.
  getLoggedInState();
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/shared/vue/logged-in-user.js